import React from "react"
import { Calendar, ClipboardList, MapPin } from "lucide-react"
import Badge from "../common/Badge.jsx"
import useInfiniteScrollTrigger from "../hooks/useInfiniteScrollTrigger.js"
import { getManifestStatusBadgeStyle } from "../utils/manifestStatusBadge.js"

function hexToRgba(hex, alpha) {
    const raw = String(hex ?? "").replace("#", "").trim()
    if (raw.length !== 6) {
        return `rgba(0, 0, 0, ${alpha})`
    }
    const r = parseInt(raw.slice(0, 2), 16)
    const g = parseInt(raw.slice(2, 4), 16)
    const b = parseInt(raw.slice(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * @param {{
 *   manifests: any[],
 *   hasMore?: boolean,
 *   isLoading?: boolean,
 *   isLoadingInitial?: boolean,
 *   onLoadMore?: () => void,
 *   onManifestClick?: (manifest: any) => void,
 *   manifestStatusColor?: (status: any) => string,
 * }} props
 */
export default function ManifestsMobileList({
    manifests,
    hasMore = false,
    isLoading = false,
    isLoadingInitial = false,
    onLoadMore = () => {},
    onManifestClick = (_manifest) => {},
    manifestStatusColor = /** @type {((status: any) => string) | undefined} */ (undefined),
    selectedManifests = undefined,
    onSelectedManifestsChange = undefined,
}) {
    const sentinelRef = useInfiniteScrollTrigger({
        enabled: true,
        hasMore,
        isLoading,
        onLoadMore,
    })

    if (isLoadingInitial && manifests.length === 0) {
        return (
            <div className="d-lg-none v2-mobile-list">
                <div className="v2-mobile-list-row bg-white rounded p-3 mb-3 text-secondary w-100">
                    Loading manifests...
                </div>
            </div>
        )
    }

    return (
        <div className="d-lg-none v2-mobile-list">
            {manifests.map((manifest) => (
                (() => {
                    const statusColor = manifestStatusColor?.(manifest.rawStatus ?? manifest.status)
                    const selected = Array.isArray(selectedManifests) ? selectedManifests : []
                    const isSelectable = typeof onSelectedManifestsChange === "function"
                    const isChecked = isSelectable && selected.some((m) => String(m?.id) === String(manifest?.id))
                    return (
                <div
                    key={manifest.id}
                    className={
                        "v2-mobile-list-row bg-white p-3 border-bottom position-relative w-100"
                    }
                    style={{
                        cursor: "pointer",
                        backgroundColor: isChecked ? "rgba(15, 97, 112, 0.08)" : undefined,
                    }}
                    onClick={() => onManifestClick(manifest)}
                >
                    <div className="d-flex justify-content-between align-items-start gap-2 w-100">
                        <div className="min-w-0 flex-grow-1">
                            <h3 className="fw-semibold mb-0 text-truncate" style={{ color: "#0f6170" }}>
                                #{manifest.manifestNumber}
                            </h3>
                            <div
                                className="d-flex align-items-center text-secondary mt-1 min-w-0"
                                style={{ fontSize: "0.875rem" }}
                            >
                                <MapPin size={14} className="me-1 flex-shrink-0" />
                                <span className="text-truncate">{manifest.location}</span>
                            </div>
                        </div>
                        <div className="flex-shrink-0">
                        {statusColor ? (
                            <Badge
                                style={{
                                    color: statusColor,
                                    borderColor: statusColor,
                                    backgroundColor: hexToRgba(statusColor, 0.16),
                                }}
                            >
                                {manifest.status}
                            </Badge>
                        ) : (
                            <Badge style={getManifestStatusBadgeStyle(manifest.rawStatus ?? manifest.status)}>
                                {manifest.status}
                            </Badge>
                        )}
                        </div>
                    </div>

                    <div
                        className="d-flex align-items-center text-secondary mt-2"
                        style={{ fontSize: "0.875rem" }}
                    >
                        <ClipboardList size={14} className="me-1" />
                        {manifest.numOrders} orders
                    </div>

                    <div
                        className="d-flex align-items-center text-secondary mt-1"
                        style={{ fontSize: "0.875rem" }}
                    >
                        <Calendar size={14} className="me-1" />
                        Created: {manifest.createdDate}
                    </div>
                    <div
                        className="d-flex align-items-center text-secondary mt-1"
                        style={{ fontSize: "0.875rem" }}
                    >
                        <Calendar size={14} className="me-1" />
                        Transferred: {manifest.transferredDate}
                    </div>
                    <div
                        className="d-flex align-items-center text-secondary mt-1"
                        style={{ fontSize: "0.875rem" }}
                    >
                        <Calendar size={14} className="me-1" />
                        Received: {manifest.receivedDate}
                    </div>

                    {isSelectable ? (
                        <div
                            className="position-absolute bottom-0 end-0 p-2"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <label
                                className="d-flex align-items-center justify-content-center rounded-circle border shadow-sm"
                                style={{
                                    width: "44px",
                                    height: "44px",
                                    background: "rgba(255,255,255,0.92)",
                                    cursor: "pointer",
                                }}
                            >
                                <input
                                    type="checkbox"
                                    className="form-check-input m-0"
                                    style={{ width: "1.25rem", height: "1.25rem" }}
                                    checked={Boolean(isChecked)}
                                    onChange={(event) => {
                                        const nextChecked = event.target.checked
                                        const next = nextChecked
                                            ? [...selected, manifest]
                                            : selected.filter((m) => String(m?.id) !== String(manifest?.id))
                                        onSelectedManifestsChange(next)
                                    }}
                                    aria-label={`Select manifest ${manifest?.id ?? ""}`}
                                />
                            </label>
                        </div>
                    ) : null}
                </div>
                    )
                })()
            ))}
            <div ref={sentinelRef} style={{ height: "1px" }} />
            {isLoading ? (
                <div className="text-center text-secondary py-3" style={{ fontSize: "0.875rem" }}>
                    Loading more...
                </div>
            ) : null}
            {!hasMore && manifests.length > 0 ? (
                <div className="text-center text-secondary py-3" style={{ fontSize: "0.875rem" }}>
                    End of manifests
                </div>
            ) : null}
        </div>
    )
}

